import { useMutation } from '@tanstack/react-query';

const normalizeLanguage = (lang) => (lang === 'es' ? 'es' : 'en');

const buildRegistrationPayload = ({
  first = '',
  last = '',
  email = '',
  phone = '',
  username = '',
  password = '',
  orgId = '',
  lang = '',
  facilitator = false,
}) => ({
  first,
  last,
  email,
  phone,
  username,
  password,
  orgId,
  lang: normalizeLanguage(lang),
  facilitator,
});

const normalizeRegistrationToken = (value) => {
  if (typeof value !== 'string') {
    return '';
  }

  const trimmedValue = value.trim();

  // Some environments return the token as a JSON string body, others as plain text.
  if (
    trimmedValue.startsWith('"') &&
    trimmedValue.endsWith('"') &&
    trimmedValue.length >= 2
  ) {
    try {
      const parsedValue = JSON.parse(trimmedValue);
      return typeof parsedValue === 'string' ? parsedValue : trimmedValue;
    } catch (_error) {
      return trimmedValue.slice(1, -1);
    }
  }

  return trimmedValue;
};

const registerUser = async ({
  first,
  last,
  email,
  phone,
  username,
  password,
  orgId,
  lang,
  facilitator,
}) => {
  const payload = buildRegistrationPayload({
    first,
    last,
    email,
    phone,
    username,
    password,
    orgId,
    lang,
    facilitator,
  });

  const response = await fetch(`${process.env.REACT_APP_API_URL}/api/registration`, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(await response.text());
  }

  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    const data = await response.json();

    if (typeof data === 'string') {
      return normalizeRegistrationToken(data);
    }

    return normalizeRegistrationToken(data?.token || '');
  }

  return normalizeRegistrationToken(await response.text());
};

const useRegistrationMutation = () =>
  useMutation({
    mutationFn: registerUser,
  });

export default useRegistrationMutation;
